import { EmptyState } from 'module/ui/components/atoms/empty-state';
import { faHexagonExclamation } from '@fortawesome/pro-regular-svg-icons';
import { Icon, Loader } from '@rocketlane/ui';
import {
  Captions,
  MediaErrorDetail,
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  Track,
  useMediaRemote,
  useMediaState,
  useMediaStore,
} from '@vidstack/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useVideoPlayerContext } from '../player-context';
import { transcriptToVTT } from '../transcript-to-vtt';
import { Transcript } from '../types';
import { Controls } from './controls/controls';
import { useErrorHandler } from './error-handler';

interface PlayerProps {
  videoSrc?: string;
  onError?: (
    error: MediaErrorDetail,
  ) => Promise<{ canResumePlayback: boolean }>;
  className?: string;
  transcript?: Transcript;
}

export const VideoPlayer = ({ videoSrc, transcript, onError }: PlayerProps) => {
  const {
    onReady,
    updateCurrentTime,
    updateDuration,
    updateCanPlay,
    currentTime: currentTimeFromContext,
    duration: durationFromContext,
    resetVideoPlayerProps,
    setPlayerRef,
  } = useVideoPlayerContext();
  const playerRef = useRef<MediaPlayerInstance>(null);

  useEffect(() => {
    setPlayerRef(playerRef);

    return () => {
      setPlayerRef(null);
    };
  }, [setPlayerRef]);

  const canPlay = useMediaState('canPlay', playerRef);
  const currentTime = useMediaState('currentTime', playerRef);
  const duration = useMediaState('duration', playerRef);
  const isSeeking = useMediaState('seeking', playerRef);
  const isWaiting = useMediaState('waiting', playerRef);
  const isPlaying = useMediaState('playing', playerRef);
  const isFullscreen = useMediaState('fullscreen', playerRef);
  const remote = useMediaRemote(playerRef);
  const mediaStore = useMediaStore(playerRef);
  const textTrack = mediaStore?.textTrack;
  const isCaptionsEnabled = textTrack?.mode === 'showing';
  const vttContent = useMemo(() => {
    if (transcript) {
      return transcriptToVTT(transcript);
    }

    return null;
  }, [transcript]);

  useEffect(() => {
    if (currentTime !== currentTimeFromContext) {
      updateCurrentTime(currentTime);
    }
  }, [currentTime, currentTimeFromContext, updateCurrentTime]);

  useEffect(() => {
    if (duration > 0 && duration !== durationFromContext) {
      updateDuration(duration);
    }
  }, [duration, durationFromContext, updateDuration]);

  useEffect(() => {
    updateCanPlay(canPlay);
  }, [canPlay, updateCanPlay]);

  useEffect(() => {
    if (canPlay) {
      onReady(remote);
    }
  }, [canPlay, onReady, remote]);
  useEffect(() => {
    return () => {
      resetVideoPlayerProps();
    };
  }, [resetVideoPlayerProps]);

  const isMediaLoading = useMemo(() => {
    return isSeeking || !canPlay || isWaiting;
  }, [isSeeking, canPlay, isWaiting]);

  const { showErrorInUI } = useErrorHandler({
    playerRef,
    onError,
    videoSrc,
  });
  const handleClick = useCallback(() => {
    if (isPlaying) {
      remote.pause();
    } else {
      remote.play();
    }
  }, [remote, isPlaying]);

  return (
    <MediaPlayer
      className={`${isFullscreen ? 'rounded-0' : 'rounded-8'} overflow-hidden relative flex flex-col w-full bg-black group/recording-player`}
      src={videoSrc}
      ref={playerRef}
      preload="metadata"
    >
      <MediaProvider
        className="flex-1 flex items-center justify-center min-h-0 w-full [&_video]:w-full [&_video]:h-full [&_video]:object-contain aspect-video cursor-pointer"
        onClick={handleClick}
      />
      {vttContent && (
        <Track
          content={vttContent}
          kind="captions"
          label="English"
          lang="en"
          type="json"
        />
      )}
      {showErrorInUI ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <EmptyState
            image={
              <Icon
                icon={faHexagonExclamation}
                className="text-[var(--red-60)]"
                size="2x"
              />
            }
            message={
              <div className="text-[var(--white-70)]">
                <FormattedMessage id="account.meeting_transcript.video.error" />
              </div>
            }
          />
        </div>
      ) : (
        <>
          {isCaptionsEnabled && (
            <Captions
              className={`absolute z-1 text-white text-center rounded-8 overflow-hidden [&_[data-part="cue-display"]]:bg-black/50 [&_[data-part="cue-display"]]:px-[var(--spacing-03)] [&_[data-part="cue-display"]>:first-child]:pt-[var(--spacing-03)] [&_[data-part="cue-display"]>:last-child]:pb-[var(--spacing-03)]
              ${isFullscreen ? 'bottom-[80px] body-short-02 left-1/2 -translate-x-1/2' : 'bottom-[44px] body-short-00 left-1/2 -translate-x-1/2 max-w-[80%]'}
              `}
            />
          )}
          {isMediaLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader size={24} />
            </div>
          )}
        </>
      )}
      <Controls
        className={showErrorInUI ? 'opacity-0 pointer-events-none' : ''}
      />
    </MediaPlayer>
  );
};
